import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MapPin, ShieldCheck, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { PageHeader } from "./fordon";

const URL_SELF = "https://www.akhusbilfritid.se/husbil-lulea";

const FAQ = [
  {
    q: "Finns det husbilshandlare i Luleå?",
    a: "Det finns ett fåtal alternativ i Luleå-området. Vi på AK Husbil & Fritid ligger i Kalix, ca 55 minuters bilväg (85 km) från Luleå — och många Luleåbor väljer oss för att vi är officiell Rapido-återförsäljare och har familjeägt fokus på personlig service.",
  },
  {
    q: "Hur långt är det från Luleå till er i Kalix?",
    a: "Ca 85 km, ca 55 minuter på E4:an. Många kombinerar husbilsbesöket med en helgtur i Kalix skärgård eller Haparanda.",
  },
  {
    q: "Kan ni leverera husbilen till Luleå?",
    a: "Ja, transport ordnas mot en fast avgift. Ring oss så räknar vi ihop det tillsammans med köpet.",
  },
  {
    q: "Servar ni husbilar från Luleå?",
    a: "Absolut. Vi tar in husbilar från hela Norrbotten för fukttest, vinterservice, gasol/värmesystem, elarbeten och chassiservice. Boka i god tid inför säsong.",
  },
  {
    q: "Har ni både nya och begagnade husbilar?",
    a: "Ja. Vi säljer nya Rapido från fabrik och kontrollerade begagnade husbilar från flera märken — Rapido, Hymer, Kabe, Adria, Bürstner m.fl.",
  },
];

export const Route = createFileRoute("/husbil-lulea")({
  head: () => ({
    meta: [
      { title: "Husbilar Luleå – se lagret 55 min bort i Kalix" },
      {
        name: "description",
        content:
          "Husbilar och husvagnar för dig i Luleå: fukttestade, gasoltestade och prissatta rakt. 55 min från Luleå, Rapido-återförsäljare sedan 2004. Ring 070-862 97 43.",
      },
      { name: "keywords", content: "husbil Luleå, husbilar Luleå, köpa husbil Luleå, husvagn Luleå, husbilshandlare Luleå, begagnad husbil Luleå" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Husbil Luleå – AK Husbil & Fritid" },
      { property: "og:description", content: "Familjeägd husbilshandlare 55 min från Luleå. Officiell Rapido-återförsäljare, kontrollerade fordon." },
      { property: "og:url", content: URL_SELF },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          name: "AK Husbil & Fritid",
          url: URL_SELF,
          areaServed: ["Luleå", "Kalix", "Boden", "Piteå", "Haparanda", "Norrbotten"],
          address: {
            "@type": "PostalAddress",
            streetAddress: "Stabsvägen 5",
            addressLocality: "Kalix",
            postalCode: "952 51",
            addressCountry: "SE",
          },
          telephone: "+46708629743",
        }),
      },
    ],
  }),
  component: HusbilLuleaPage,
});

function HusbilLuleaPage() {
  return (
    <>
      <PageHeader
        label="Husbil · Luleå · Norrbotten"
        title={<>Husbil för dig i <span className="font-italic-display text-accent">Luleå</span>.</>}
        sub="AK Husbil & Fritid ligger i Kalix — 55 minuter från Luleå på E4:an. Vi är officiell Rapido-återförsäljare, familjeägda sedan 2004, och känner Norrbotten in på skinnet."
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <a href="tel:0708629743" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            <Phone size={16} /> Ring 070-862 97 43
          </a>
          <Link to="/fordon" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Se husbilar i lager <ArrowRight size={14} />
          </Link>
          <Link to="/rapido" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            Rapido-modeller
          </Link>
        </div>
      </section>

      <section className="container-edge pt-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            ["55 min", "från Luleå centrum till lagret i Kalix"],
            ["Sedan 2004", "familjeägd handlare och verkstad"],
            ["Dagligen", "uppdateras husbilarna som finns till salu"],
          ].map(([value, label]) => (
            <div key={value} className="rounded-2xl border border-border bg-card p-5">
              <p className="font-display text-3xl text-accent">{value}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">Lagerstatus uppdateras löpande via vår fordonssida.</p>
      </section>

      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="eyebrow">Från Luleå till oss</p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl">85 km. 55 minuter. <span className="font-italic-display text-accent">Värt resan.</span></h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Kalix ligger rakt norr om Luleå på E4:an. Många Luleåbor väljer oss för att vi är officiell Rapido-återförsäljare, för att våra begagnade fordon alltid är fukttestade och för att vi svarar när telefonen ringer — även i juli.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Officiell Rapido-återförsäljare för norra Sverige",
                "Kontrollerade begagnade husbilar & husvagnar",
                "Verkstad, fukttest och garantihantering",
                "Personlig service — samma människor varje gång",
                "Vi ordnar transport till Luleå vid behov",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-foreground/85">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <ShieldCheck className="text-accent" size={32} />
            <h3 className="mt-6 font-display text-3xl">Kör hit</h3>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Stabsvägen 5, 952 51 Kalix. Från Luleå: kör E4 norrut mot Haparanda i ca 85 km — Kalix ligger på vägen. Vi öppnar enligt överenskommelse; ring först.
            </p>
            <dl className="mt-8 space-y-4 text-sm">
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground flex items-center gap-2"><MapPin size={14} /> Från Luleå C</dt>
                <dd className="font-medium">85 km · 55 min</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground flex items-center gap-2"><Clock size={14} /> Öppet</dt>
                <dd className="font-medium">Enligt överenskommelse</dd>
              </div>
              <div className="flex justify-between border-b border-border pb-3">
                <dt className="text-muted-foreground">Telefon</dt>
                <dd className="font-medium">070-862 97 43</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">E-post</dt>
                <dd className="font-medium">info@akhusbilfritid.se</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <p className="eyebrow">Vanliga frågor</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">Husbil <span className="font-italic-display text-accent">Luleå</span> — vad du behöver veta.</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {FAQ.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">{f.q}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-edge py-16 md:py-24">
        <div className="rounded-3xl border border-border bg-foreground p-8 md:p-14 text-background">
          <p className="eyebrow" style={{ color: "hsl(var(--accent))" }}>Nästa steg</p>
          <h2 className="mt-3 font-display text-4xl md:text-6xl leading-[1.05]">Kör ner från Luleå — eller ring först.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Vi bokar in dig, säkerställer att modellerna du är intresserad av står framme och lägger tiden på just din användning.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-semibold text-accent-foreground hover:opacity-90">
              <Phone size={16} /> 070-862 97 43
            </a>
            <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10">
              <MapPin size={16} /> Boka besök i Kalix
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
